import { ClickAwayListener, Popper } from '@mui/material'
import { useEventCallback } from '@mui/material/utils'
import { cloneElement, useEffect, useId, useMemo, useRef } from 'react'
import { usePinnablePopover } from './usePinnablePopover'
import type { PopperProps } from '@mui/material'
import type { AriaAttributes, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, ReactElement, ReactNode } from 'react'

/** Wrappers that forward a caller's element through to `trigger` should type it as this. */
export type PinnablePopoverTrigger = ReactElement<TriggerProps>

type TriggerProps = {
  onClick?: MouseEventHandler<HTMLElement>
  onMouseEnter?: MouseEventHandler<HTMLElement>
  onMouseLeave?: MouseEventHandler<HTMLElement>
  onFocus?: FocusEventHandler<HTMLElement>
  onBlur?: FocusEventHandler<HTMLElement>
  onKeyDown?: KeyboardEventHandler<HTMLElement>
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-controls'?: AriaAttributes['aria-controls']
}

export type PinnablePopoverProps = {
  /**
   * The element that opens the popover. It is cloned with pointer/focus/keyboard handlers and
   * `aria-haspopup`/`aria-expanded`/`aria-controls`, so it must:
   *
   * 1. spread unrecognised props onto a DOM node — MUI components and intrinsic elements do;
   *    a custom component that destructures only its own props will silently never open; and
   * 2. render something focusable (`Button`, `IconButton`, `Chip` with `onClick`, or anything
   *    with a `tabIndex`), or the popover is unreachable by keyboard.
   *
   * No `ref` is required — unlike MUI's `Tooltip`, the anchor is taken from the opening event.
   */
  trigger: ReactElement<TriggerProps>
  /** Accessible name for the popover, which is exposed as a dialog. */
  label: string
  /** Rendered inside the dialog. Only mounted while open, so it may fetch its own data. */
  children: ReactNode
  placement?: PopperProps['placement']
  onOpenChange?: (isOpen: boolean) => void
}

const POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 8] } }]
// Once the popover has resolved where it fits, hold it there: content that changes height while
// open (paginated sources) must not make it flip to the other side of its trigger.
const LOCKED_POPPER_MODIFIERS = [...POPPER_MODIFIERS, { name: 'flip', enabled: false }]

export const PinnablePopover = ({ trigger, label, children, placement = 'bottom-start', onOpenChange }: PinnablePopoverProps) => {
  const {
    anchorEl,
    isOpen,
    lockedPlacement,
    lockPlacement,
    close,
    handleClick,
    handleTriggerEnter,
    scheduleClose,
    cancelClose,
    handleDialogKeyDown,
    dialogRef,
  } = usePinnablePopover()
  const dialogId = useId()
  const popperOptions = useMemo<NonNullable<PopperProps['popperOptions']>>(
    () => ({ onFirstUpdate: (state) => lockPlacement(state.placement) }),
    [lockPlacement]
  )

  const notifyOpenChange = useEventCallback((open: boolean) => onOpenChange?.(open))
  const notifiedOpenRef = useRef(isOpen)
  useEffect(() => {
    if (notifiedOpenRef.current === isOpen) return
    notifiedOpenRef.current = isOpen
    notifyOpenChange(isOpen)
  }, [isOpen, notifyOpenChange])

  const triggerWithPopoverHandlers = cloneElement(trigger, {
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      trigger.props.onClick?.(event)
      handleClick(event)
    },
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      trigger.props.onMouseEnter?.(event)
      handleTriggerEnter(event)
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      trigger.props.onMouseLeave?.(event)
      scheduleClose()
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      trigger.props.onFocus?.(event)
      handleTriggerEnter(event)
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      trigger.props.onBlur?.(event)
      scheduleClose()
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      trigger.props.onKeyDown?.(event)
      handleDialogKeyDown(event)
    },
    'aria-haspopup': 'dialog',
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? dialogId : undefined,
  })

  return (
    <>
      {triggerWithPopoverHandlers}
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement={lockedPlacement ?? placement}
        modifiers={lockedPlacement ? LOCKED_POPPER_MODIFIERS : POPPER_MODIFIERS}
        popperOptions={popperOptions}
        sx={{ zIndex: 'modal' }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            const target = event.target
            if (target instanceof Node && anchorEl?.contains(target)) return
            close()
          }}
        >
          <div
            id={dialogId}
            role="dialog"
            aria-label={label}
            tabIndex={-1}
            ref={dialogRef}
            onKeyDown={handleDialogKeyDown}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            {children}
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  )
}
